import React, { useState } from 'react';
import { detectPosition, getPlayerStyle, getPositionConfidence, calculatePlayerRating, getPositionExplanation } from '../utils/playerUtils';

const Players = ({ players, isAdmin, onDeletePlayer }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPosition, setFilterPosition] = useState('All Players');
  const [sortBy, setSortBy] = useState('rating');
  const [showDetails, setShowDetails] = useState(null);

  const positions = ['All Players', 'Forward', 'Midfielder', 'Defender', 'Goalkeeper', 'Hybrid', 'Provisional'];

  const getPlayerStats = (player) => {
    const position = detectPosition(player);
    
    if (position === 'Goalkeeper') {
      return [
        { label: 'CLEAN', value: player.clean_sheets || 0, color: 'text-purple-400' },
        { label: 'SAVES', value: player.total_saves || 0, color: 'text-blue-400' },
        { label: 'MATCHES', value: player.matches_played || 0, color: 'text-stone-400' }
      ];
    }
    return [
      { label: 'GOALS', value: player.total_goals || 0, color: 'text-rose-400' },
      { label: 'ASSISTS', value: player.total_assists || 0, color: 'text-emerald-400' },
      { label: 'MATCHES', value: player.matches_played || 0, color: 'text-stone-400' }
    ];
  };

  const getPositionIcon = (position) => {
    if (position === 'Goalkeeper') return 'front_hand';
    if (position === 'Forward') return 'sports_score';
    if (position === 'Midfielder') return 'handshake';
    if (position === 'Defender') return 'shield';
    if (position === 'Forward/Midfielder') return 'bolt';
    if (position === 'Midfielder/Defender') return 'moving';
    if (position === 'Utility Player') return 'stars';
    if (position === 'Provisional') return 'help';
    return 'person';
  };

  const playersWithPosition = players.map(p => ({
    ...p,
    autoPosition: detectPosition(p),
    playerStyle: getPlayerStyle(p),
    confidence: getPositionConfidence(p),
    rating: calculatePlayerRating(p),
    explanation: getPositionExplanation(p)
  }));

  const filteredPlayers = playersWithPosition
    .filter(player => {
      const matchesSearch = player.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      let matchesPosition = false;
      if (filterPosition === 'All Players') {
        matchesPosition = true;
      } else if (filterPosition === 'Hybrid') {
        matchesPosition = player.autoPosition.includes('/') || player.autoPosition === 'Utility Player';
      } else {
        matchesPosition = player.autoPosition === filterPosition || player.autoPosition.includes(filterPosition);
      }
      
      return matchesSearch && matchesPosition;
    })
    .sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'goals') return (b.total_goals || 0) - (a.total_goals || 0);
      if (sortBy === 'assists') return (b.total_assists || 0) - (a.total_assists || 0);
      if (sortBy === 'matches') return (b.matches_played || 0) - (a.matches_played || 0);
      return 0;
    });

  const positionStats = {
    Forward: playersWithPosition.filter(p => p.autoPosition === 'Forward').length,
    Midfielder: playersWithPosition.filter(p => p.autoPosition === 'Midfielder').length,
    Defender: playersWithPosition.filter(p => p.autoPosition === 'Defender').length,
    Goalkeeper: playersWithPosition.filter(p => p.autoPosition === 'Goalkeeper').length,
    Hybrid: playersWithPosition.filter(p => p.autoPosition.includes('/') || p.autoPosition === 'Utility Player').length,
    Provisional: playersWithPosition.filter(p => p.autoPosition === 'Provisional').length
  };

  if (players.length === 0) {
    return (
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-16 text-center shadow-lg animate-fadeIn">
        <div className="w-20 h-20 bg-background rounded-full flex items-center justify-center mx-auto mb-6 border border-outline-variant/30 shadow-inner">
          <span className="material-symbols-outlined text-4xl text-stone-600">groups</span>
        </div>
        <h3 className="text-3xl sm:text-4xl font-black text-on-background font-lexend mb-3">No Personnel Found</h3>
        <p className="text-stone-400 font-manrope max-w-md mx-auto text-lg">Squad roster will populate once match data is imported into the system.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn pb-24">
      {/* AI Context Banner */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex items-start gap-4">
        <span className="material-symbols-outlined text-primary mt-0.5">smart_toy</span>
        <div>
          <h4 className="text-primary font-lexend font-bold text-sm mb-1">Algorithm Notice</h4>
          <p className="text-stone-400 font-manrope text-sm leading-relaxed">
            Positional archetypes are dynamically assigned based on match telemetry. A minimum of 3 fixtures is required for high-confidence classification. Hybrid designations indicate multi-role impact.
          </p>
        </div>
      </div>

      {/* Header & Stats Grid */}
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">Active Roster</h2>
            <p className="text-stone-400 text-sm font-manrope">System-wide personnel overview</p>
          </div>
          <div className="text-right">
            <p className="text-primary text-[10px] font-bold uppercase tracking-widest font-manrope mb-1">Total Operators</p>
            <p className="text-on-background text-3xl font-black font-lexend leading-none">{players.length}</p>
          </div>
        </div>

        {/* Position Distribution Cards */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {Object.entries(positionStats).map(([pos, count]) => {
            const colors = {
              Forward: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
              Midfielder: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
              Defender: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
              Goalkeeper: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
              Hybrid: 'text-primary bg-primary/10 border-primary/20',
            };
            const theme = colors[pos] || 'text-stone-400 bg-background border-outline-variant/30';
            
            return (
              <div key={pos} className={`rounded-xl p-3 text-center border ${theme} transition-all hover:scale-[1.02]`}>
                <p className="text-2xl font-black font-lexend mb-1">{count}</p>
                <p className="text-[9px] font-bold uppercase tracking-widest font-manrope opacity-80">{pos}</p>
              </div>
            );
          })}
        </div>

        {/* Search Input */}
        <div className="relative mb-6">
          <span className="material-symbols-outlined absolute left-5 top-1/2 -translate-y-1/2 text-stone-500">search</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Query operator database..."
            className="w-full bg-background border border-outline-variant/50 rounded-2xl pl-14 pr-5 py-4 text-on-background font-manrope placeholder:text-stone-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
          />
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-3 overflow-x-auto pb-2 custom-scrollbar">
          {positions.map(position => (
            <button
              key={position}
              onClick={() => setFilterPosition(position)}
              className={`px-5 py-2.5 rounded-xl font-manrope font-bold text-xs whitespace-nowrap transition-all active:scale-95 ${
                filterPosition === position
                  ? 'bg-primary text-on-primary shadow-lg shadow-primary/20 border border-primary'
                  : 'bg-background text-stone-400 hover:text-on-background hover:bg-surface-container-high border border-outline-variant/30'
              }`}
            >
              {position} {position !== 'All Players' && `(${positionStats[position] || 0})`}
            </button>
          ))}
        </div>
      </div>

      {/* Sorting Control */}
      <div className="flex items-center justify-between px-2">
        <p className="text-stone-500 font-manrope text-xs font-bold uppercase tracking-widest">{filteredPlayers.length} Results</p>
        <button
          onClick={() => setSortBy(
            sortBy === 'rating' ? 'goals' : 
            sortBy === 'goals' ? 'assists' : 
            sortBy === 'assists' ? 'matches' : 'rating'
          )}
          className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-manrope text-xs font-bold uppercase tracking-widest"
        >
          Sort by: {sortBy}
          <span className="material-symbols-outlined text-[16px]">sort</span>
        </button>
      </div>

      {/* Players List Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredPlayers.map((player, index) => {
          const stats = getPlayerStats(player);
          const iconName = getPositionIcon(player.autoPosition);
          
          return (
            <div
              key={player.id}
              className="bg-surface-container border border-outline-variant/20 rounded-3xl p-5 shadow-lg hover:bg-surface-container-high transition-all group cursor-pointer"
              onClick={() => setShowDetails(showDetails === player.id ? null : player.id)}
            >
              <div className="flex items-start gap-5">
                
                {/* Avatar & Rank */}
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 bg-surface-container-highest rounded-2xl flex items-center justify-center border border-outline-variant/30">
                    <span className="material-symbols-outlined text-3xl text-stone-300" style={{fontVariationSettings: "'FILL' 1"}}>{iconName}</span>
                  </div>
                  {index < 3 && sortBy === 'rating' && (
                    <div className="absolute -bottom-2 -right-2 w-7 h-7 bg-primary rounded-xl flex items-center justify-center border-2 border-surface-container">
                      <span className="text-on-primary text-xs font-black font-lexend">{index + 1}</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-on-background font-lexend font-bold text-xl truncate pr-2">
                      {player.name}
                    </h3>
                    {isAdmin && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeletePlayer(player.id); }}
                        className="text-stone-500 hover:text-error transition-colors p-1 opacity-0 group-hover:opacity-100"
                        title="Purge Operator"
                      >
                        <span className="material-symbols-outlined text-[20px]">delete</span>
                      </button>
                    )}
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-[10px] px-2.5 py-1 rounded-lg font-bold font-manrope uppercase tracking-wider bg-background border border-outline-variant/50 text-stone-300">
                      {player.autoPosition}
                    </span>
                    <span className="text-[10px] px-2.5 py-1 rounded-lg font-bold font-manrope uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                      {player.playerStyle}
                    </span>
                    {player.confidence > 0 && (
                      <span className={`text-[10px] px-2.5 py-1 rounded-lg font-bold font-manrope uppercase tracking-wider border ${
                        player.confidence >= 80 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                        player.confidence >= 60 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                        'bg-rose-500/10 text-rose-400 border-rose-500/20'
                      }`}>
                        {player.confidence}% Conf
                      </span>
                    )}
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-4 gap-2 pt-4 border-t border-outline-variant/10 text-center">
                    {stats.map((stat, idx) => (
                      <div key={idx}>
                        <p className={`${stat.color} font-lexend font-black text-xl leading-none mb-1`}>{stat.value}</p>
                        <p className="text-stone-500 font-manrope text-[9px] uppercase tracking-widest font-bold">{stat.label}</p>
                      </div>
                    ))}
                    <div className="bg-background rounded-lg py-1 border border-outline-variant/30">
                      <p className="text-primary font-lexend font-black text-xl leading-none mb-1">{player.rating}</p>
                      <p className="text-stone-500 font-manrope text-[9px] uppercase tracking-widest font-bold">RTG</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Diagnostic Info */}
              {showDetails === player.id && (
                <div className="mt-4 pt-4 border-t border-outline-variant/20 animate-fadeIn">
                  <div className="bg-background rounded-xl p-4 border border-outline-variant/30">
                    <div className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-sm mt-0.5">info</span>
                      <div>
                        <p className="text-primary font-manrope text-[10px] font-bold uppercase tracking-widest mb-1">System Analysis</p>
                        <p className="text-stone-300 font-manrope text-sm">{player.explanation}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* No Results */}
      {filteredPlayers.length === 0 && (
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-16 text-center shadow-lg">
          <span className="material-symbols-outlined text-4xl text-stone-600 mb-4">person_off</span>
          <h3 className="text-2xl font-bold text-on-background font-lexend mb-2">No matching personnel</h3>
          <p className="text-stone-400 font-manrope">Adjust your search parameters or positional filters.</p>
        </div>
      )}
    </div>
  );
};

export default Players;
